<script setup lang="ts">
/*
 * Right flat side sheet, opened from the header cart button. Shows the cart
 * store as it is (lines as small tiles, subtotal, per-order limit) and hands
 * off to /checkout or /cart. Removal goes through useCartRemove (undo toast).
 */
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { LogIn, ShoppingCart, X } from 'lucide-vue-next';
import EmptyState from '../EmptyState.vue';
import SideDrawer from '../shell/SideDrawer.vue';
import MiniCartLine from './MiniCartLine.vue';
import { useAuthStore } from '../../stores/auth';
import { KINGUIN_ORDER_LIMIT, useCartStore } from '../../stores/cart';
import { useUiStore } from '../../stores/ui';
import PriceTag from '../PriceTag.vue';

const { t } = useI18n();
const auth = useAuthStore();
const cart = useCartStore();
const ui = useUiStore();
</script>

<template>
  <SideDrawer
    side="right"
    width-class="w-[420px]"
    :open="ui.miniCartOpen"
    :label="t('header.aria.cart', { count: cart.count })"
    @close="ui.closeMiniCart()"
  >
    <template #default="{ close }">
      <div class="flex h-16 shrink-0 items-center justify-between border-b border-border pl-5 pr-3">
        <div class="flex items-baseline gap-3">
          <h2 class="font-heading text-[17px] font-black text-text-base">{{ t('common.miniCart.title', { count: cart.count }) }}</h2>
          <span v-if="auth.isAuthenticated && cart.count > 0" class="text-[12px] tabular-nums text-text-dim">
            {{ t('common.miniCart.limit', { count: cart.count, limit: KINGUIN_ORDER_LIMIT }) }}
          </span>
        </div>
        <button type="button" class="btn-icon" :aria-label="t('common.actions.close')" @click="close">
          <X class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-5">
        <EmptyState
          v-if="!auth.isAuthenticated"
          class="my-6"
          :icon="LogIn"
          :title="t('common.miniCart.signedOutTitle')"
          :body="t('common.miniCart.signedOutBody')"
          to="/login"
          :action-label="t('common.actions.signIn')"
        />
        <EmptyState
          v-else-if="cart.isEmpty"
          class="my-6"
          :icon="ShoppingCart"
          :title="t('common.miniCart.emptyTitle')"
          :body="t('common.miniCart.emptyBody')"
          to="/catalog"
          :action-label="t('common.miniCart.browse')"
        />
        <ul v-else class="flex flex-col gap-3 py-4">
          <MiniCartLine v-for="item in cart.items" :key="cart.keyOf(item)" :item="item" />
        </ul>
      </div>

      <div v-if="auth.isAuthenticated && !cart.isEmpty" class="shrink-0 border-t border-border bg-bg-base p-5">
        <div class="mb-4 flex items-end justify-between">
          <span class="text-eyebrow">{{ t('common.label.subtotal') }}</span>
          <PriceTag :cents="cart.subtotalCents" size="hud" class="text-[32px] text-text-base" />
        </div>
        <RouterLink to="/checkout" class="btn-primary w-full">{{ t('common.miniCart.checkout') }}</RouterLink>
        <RouterLink to="/cart" class="btn-secondary mt-2 w-full">{{ t('common.toast.viewCart') }}</RouterLink>
      </div>
    </template>
  </SideDrawer>
</template>
